import React, { useState, useEffect, useCallback } from 'react';

import { Button, Input, Popover } from 'antd';

import styled from 'styled-components';

import { useEventsSearchContext, useEventsSearchContextDispatch } from '@contexts/EventsSearchContext';

import Switch from '@components/Gui/CustomSwitch';
import FlexContainer from '@components/Gui/FlexContainer';
import MiniLoading from '@components/Gui/MiniLoading';
import NwButtonOption from '@components/Gui/NwButtonOption';
import NwIcon from '@components/Gui/NwIcon';

import { byPrefixAndName } from '@awesome.me/kit-d6dae216cd/icons';
import debounce from 'lodash/debounce';

const ButtonGroup = Button.Group;

const StyledHeader = styled.div`
    margin: 0;
    padding: 0;
    position: relative;
    background: #fff;
    border-radius: 6px;
    border: 1px solid #f0f0f0;
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: flex-start;
    min-height: ${(props) => !props.drawerMode && '5rem'};
    padding: 1em;
    font-size: 0.9rem;
    margin-bottom: 1rem;

    @media only screen and (max-width: 960px) {
        flex-direction: column;
        align-items: flex-start;
        justify-content: flex-start;
    }

    .header-summary {
        min-height: 5rem;
        border-right: 1px solid #e8e8e8;
        flex: auto 0 0;
        padding: 0 1em 0 0;

        @media only screen and (max-width: 960px) {
            border-right: none;
            min-height: 0;
            padding: 0;
        }

        .header-summary-text {
            font-size: 1em;
            display: flex;
            align-items: center;

            em.counter {
                font-size: 1.6em;
                font-weight: bold;
                font-style: normal;
                padding-right: 0.5em;
            }
        }
        small.hidden-models {
            display: block;
        }
    }

    .header-filters {
        min-height: 5rem;
        border-right: 1px solid #e8e8e8;
        flex: 1 0 0;
        padding: 0 1em 0 1em;

        @media only screen and (max-width: 960px) {
            border-right: none;
            min-height: 0;
            padding: 0;
            flex: none;
            align-items: flex-start;
        }

        .header-filters-first-row,
        .header-filters-second-row {
            align-items: flex-start;
            .control {
                padding-right: 1em;
            }

            @media only screen and (max-width: 640px) {
                flex-direction: column;
                padding: 0;
            }
        }
    }

    .header-actions {
        min-height: 5rem;
        flex: auto 0 0;
        padding: 0 0 0 1em;
        min-width: 280px;
        text-transform: uppercase;

        @media only screen and (max-width: 960px) {
            min-height: 0;
            padding: 0;
            min-width: 0;
        }
    }

    div.control {
        padding: 0.5em 0;
        span.label {
            font-size: 0.85em;
            text-transform: uppercase;
            padding: 0 0.5em 0 0;

            &.on-right {
                padding: 0 0 0 0.5em;
            }
        }
    }

    .loading-title {
        margin-left: 8px;
        margin-bottom: 0;
    }
`;

const EventsSearchHeader = ({ events, eventsGrouped, loading }) => {
    const { sort, showTransactions, nameFilter, groupByProject } = useEventsSearchContext();
    const dispatch = useEventsSearchContextDispatch();

    // If sort is "type" and groupByProject is active, change to "time-down"
    useEffect(() => {
        if (groupByProject && sort === 'type') {
            dispatch({ type: 'SetSort', sort: 'time-down' });
        }
    }, [groupByProject, sort, dispatch]);

    const handleChangeSort = (newSort) => () => {
        dispatch({ type: 'SetSort', sort: newSort });
    };

    const handleChangeNameFilter = (event) => {
        dispatch({ type: 'SetNameFilter', nameFilter: event.target.value });
    };

    const handleGroupEvents = (checked) => {
        dispatch({ type: 'SetGroupByProject', groupByProject: checked });
    };

    const handleShowTransactions = (checked) => {
        dispatch({ type: 'SetShowTransactions', showTransactions: checked });
    };

    const hasEvents = events && events.length > 0;

    const castingCount = events.filter((event) => event.type.toLowerCase() === 'casting').length;
    const jobCount = events.filter((event) => event.type.toLowerCase() === 'job').length;
    const extCount = events.filter((event) => event.type.toLowerCase() === 'ext').length;
    const travelCount = events.filter((event) => event.type.toLowerCase() === 'travel').length;
    const followupCount = events.filter((event) => event.type.toLowerCase() === 'followup').length;
    const externalJobCount = events.filter((event) => event.type.toLowerCase() === 'extjob').length;
    const meetingCount = events.filter((event) => event.type.toLowerCase() === 'meeting').length;
    const accommodationsCount = events.filter((event) => event.type.toLowerCase() === 'accommodation').length;

    const countDetails = [
        `${castingCount} Castings`,
        `${jobCount} Jobs`,
        `${extCount} Ext Jobs`,
        `${travelCount} Travels`,
        `${followupCount} Activities`,
        `${externalJobCount} Moves`,
        `${accommodationsCount} Accommodations`,
        `${meetingCount} Meetings`,
    ];

    // Debounced name filter state
    const [debouncedName, setDebouncedName] = useState(nameFilter);

    useEffect(() => {
        setDebouncedName(nameFilter);
    }, [nameFilter]);

    // Use lodash debounce for the filter
    const debouncedChange = useCallback(
        debounce((value) => {
            if (value !== nameFilter) {
                handleChangeNameFilter({ target: { value } });
            }
        }, 500),
        [nameFilter]
    );

    useEffect(() => {
        debouncedChange(debouncedName);
        return () => {
            debouncedChange.cancel();
        };
    }, [debouncedName, debouncedChange]);

    return (
        <StyledHeader>
            <div className="header-summary">
                {!loading ? (
                    <>
                        <div className="header-summary-text">
                            <em className="counter">{`${events.length} EVENTS FOUND`}</em>
                        </div>
                        <Popover
                            content={
                                <>
                                    {countDetails.map((countDetail) => (
                                        <div key={countDetail}>{countDetail}</div>
                                    ))}
                                </>
                            }
                        >
                            <div className="nw-link">details</div>
                        </Popover>
                    </>
                ) : (
                    <FlexContainer alignItems="center">
                        <div className="loading-container">
                            <MiniLoading />
                        </div>
                        <p className="loading-title">Fetching Events...</p>
                    </FlexContainer>
                )}
            </div>
            <div className="header-filters">
                <FlexContainer className="header-filters-first-row">
                    <div className="control">
                        <Input
                            allowClear
                            autoComplete="off"
                            placeholder="Filter by name"
                            prefix={<NwIcon icon={byPrefixAndName.fasl['search']} />}
                            value={debouncedName}
                            onChange={(e) => setDebouncedName(e.target.value)}
                        />
                    </div>
                    {hasEvents && (
                        <div className="control">
                            <ButtonGroup size="small" style={{ zIndex: '0' }}>
                                <NwButtonOption active={sort === 'time-up'} onClick={handleChangeSort('time-up')}>
                                    TIME &nbsp;
                                    <NwIcon icon={byPrefixAndName.fasl['arrow-up']} />
                                </NwButtonOption>
                                <NwButtonOption active={sort === 'time-down'} onClick={handleChangeSort('time-down')}>
                                    TIME &nbsp;
                                    <NwIcon icon={byPrefixAndName.fasl['arrow-down']} />
                                </NwButtonOption>
                                {!groupByProject && (
                                    <NwButtonOption active={sort === 'type'} onClick={handleChangeSort('type')}>
                                        TYPE
                                    </NwButtonOption>
                                )}
                            </ButtonGroup>
                        </div>
                    )}
                </FlexContainer>
            </div>
            <div className="header-actions">
                <p>
                    <Switch
                        text="group by project"
                        checked={eventsGrouped}
                        onChange={handleGroupEvents}
                        disabled={!hasEvents}
                    />
                </p>
                <p>
                    <Switch
                        text="Show fees and expenses"
                        disabled={!eventsGrouped || !hasEvents}
                        checked={showTransactions}
                        onChange={handleShowTransactions}
                    />
                </p>
            </div>
        </StyledHeader>
    );
};

export default EventsSearchHeader;
